import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { createExam, getExamAndScheduleById, updateExam } from '../../api/examApi';
import { getAllReadings } from '../../api/readingApi';
import { getAllListenings } from '../../api/listeningApi';
import { getAllWritings } from '../../api/writingApi';
import { getAllPartners } from '../../api/partnerApi';
import {
  Box,
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Grid,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  Fade,
  Zoom,
  Alert,
  Tooltip,
  Stack,
  useTheme,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';

import { useToast } from '../../contexts/ToastContext';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import ModernButton from '../../components/common/ModernButton';
import ModernTextField from '../../components/common/ModernTextField';

const ExamForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const theme = useTheme();
  // Skip the initial schedule auto-fill only when editing an existing exam
  const initialLoad = useRef(Boolean(id));
  const validationInit = useRef(true);

  const formatForDisplay = (date) => {
    if (!date) return '';
    return format(new Date(date), 'dd/MM/yyyy hh:mm a');
  };

  const addMinutes = (dateVal, mins) => {
    const d = new Date(dateVal);
    d.setMinutes(d.getMinutes() + mins);
    return d;
  };

  const localToUTC = (localDate) => {
    if (!localDate) return null;
    
    // Ensure we have a valid Date object
    const date = new Date(localDate);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date provided:', localDate);
      return null;
    }
    
    console.log('Date conversion:', {
      original: localDate,
      dateObject: date,
      isoString: date.toISOString()
    });
    
    // Simply return the ISO string - the server should handle timezone conversion
    return date.toISOString();
  };

  const generateSchedule = (start) => {
    const listeningStart = addMinutes(start, 1);
    const listeningEnd = addMinutes(listeningStart, 30);
    const readingStart = addMinutes(listeningEnd, 1);
    const readingEnd = addMinutes(readingStart, 60);
    const writingStart = addMinutes(readingEnd, 1);
    const writingEnd = addMinutes(writingStart, 60);

    return [
      { sectionType: 'listening', startTime: listeningStart, endTime: listeningEnd },
      { sectionType: 'reading', startTime: readingStart, endTime: readingEnd },
      { sectionType: 'writing', startTime: writingStart, endTime: writingEnd },
    ];
  };

  const defaultSchedule = [
    { sectionType: 'listening', startTime: null, endTime: null },
    { sectionType: 'reading', startTime: null, endTime: null },
    { sectionType: 'writing', startTime: null, endTime: null },
  ];

  const [exam, setExam] = useState({
    name: '',
    startDate: null,
    endDate: null,
    note: '',
    reading: '',
    listening: '',
    writing: '',
    isCompleted: false,
    isActive: false,
    isNext: false,
    examType: 'paid-scheduled',
    region: 'worldwide',
    scope: 'PUBLIC',
    partnerId: '',
    contentBatchId: '',
    schedule: defaultSchedule,
  });

  const [isAutoSchedule, setIsAutoSchedule] = useState(true);
  const [readingOptions, setReadingOptions] = useState([]);
  const [listeningOptions, setListeningOptions] = useState([]);
  const [writingOptions, setWritingOptions] = useState([]);
  const [partnerOptions, setPartnerOptions] = useState([]);

  // Clear any stale IDs once option lists load to avoid MUI out-of-range warnings
  useEffect(() => {
    if (readingOptions.length && exam.reading && !readingOptions.some((o) => o._id === exam.reading)) {
      setExam((prev) => ({ ...prev, reading: '' }));
    }
  }, [readingOptions]);

  useEffect(() => {
    if (listeningOptions.length && exam.listening && !listeningOptions.some((o) => o._id === exam.listening)) {
      setExam((prev) => ({ ...prev, listening: '' }));
    }
  }, [listeningOptions]);

  useEffect(() => {
    if (writingOptions.length && exam.writing && !writingOptions.some((o) => o._id === exam.writing)) {
      setExam((prev) => ({ ...prev, writing: '' }));
    }
  }, [writingOptions]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(Boolean(id));
  const [formProgress, setFormProgress] = useState(0);
  const [completedSections, setCompletedSections] = useState({});

  // Calculate form completion percentage
  useEffect(() => {
    const requiredFields = ['name', 'startDate', 'endDate', 'reading', 'listening', 'writing'];
    const filledFields = requiredFields.filter(field => {
      const value = exam[field];
      return value && value !== '';
    });
    const progress = (filledFields.length / requiredFields.length) * 100;
    setFormProgress(progress);

    // Update completed sections
    const sections = {
      basic: exam.name && exam.startDate && exam.endDate && !errors.name && !errors.startDate && !errors.endDate,
      content: exam.reading && exam.listening && exam.writing && !errors.reading && !errors.listening && !errors.writing,
      schedule: exam.startDate && exam.schedule.every(s => s.startTime && s.endTime),
    };
    setCompletedSections(sections);
  }, [exam, errors]);

  useEffect(() => {
    if (id) {
      const fetchExam = async () => {
        try {
          const { data } = await getExamAndScheduleById(id);
          console.log('Loaded exam data:', data);
          console.log('Date values from API:', {
            startDate: data.startDate,
            endDate: data.endDate,
            startDateType: typeof data.startDate,
            endDateType: typeof data.endDate
          });
          setExam({
            name: data.name,
            startDate: data.startDate ? new Date(data.startDate) : null,
            endDate: data.endDate ? new Date(data.endDate) : null,
            note: data.note,
            reading: data.reading?._id || data.reading || '',
            listening: data.listening?._id || data.listening || '',
            writing: data.writing?._id || data.writing || '',
            isCompleted: data.isCompleted || false,
            isActive: data.isActive || false,
            isNext: data.isNext || false,
            examType: data.examType || 'paid-scheduled',
            region: data.region || 'worldwide',
            scope: data.scope || 'PUBLIC',
            partnerId: data.partnerId?._id || data.partnerId || '',
            contentBatchId: data.contentBatchId ?? '',
            schedule: data.schedule ? data.schedule.map((s) => ({
              sectionType: s.sectionType,
              startTime: s.startTime ? new Date(s.startTime) : null,
              endTime: s.endTime ? new Date(s.endTime) : null,
            })) : defaultSchedule,
          });
        } catch (error) {
          console.error('Failed to fetch exam:', error);
      showToast("Failed to fetch exam data", "error");
        } finally {
          setLoading(false);
        }
      };
      fetchExam();
    }
  }, [id]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [readingRes, listeningRes, writingRes, partnersRes] = await Promise.all([
          getAllReadings(),
          getAllListenings(),
          getAllWritings(),
          getAllPartners(),
        ]);
        setReadingOptions(readingRes.data);
        setListeningOptions(listeningRes.data);
        setWritingOptions(writingRes);
        setPartnerOptions(partnersRes.data || []);
      } catch (error) {
        console.error('Failed to fetch options:', error);
        showToast("Failed to fetch options", "error");
      }
    };
    fetchOptions();
  }, []);

  const validate = (fieldValues = exam, touchedFields = touched) => {
    const errs = {};

    if (touchedFields.name && !fieldValues.name) errs.name = 'Required';
    if (touchedFields.startDate) {
      if (!fieldValues.startDate) errs.startDate = 'Required';
      // Only validate past date for new exams, not when editing existing ones
      else if (!id && new Date(fieldValues.startDate) < new Date()) errs.startDate = 'Start date cannot be in the past';
    }
    if (touchedFields.endDate) {
      if (!fieldValues.endDate) errs.endDate = 'Required';
      else if (fieldValues.startDate && new Date(fieldValues.startDate) >= new Date(fieldValues.endDate)) errs.endDate = 'End date must be after start date';
    }
    if (touchedFields.reading && !fieldValues.reading) errs.reading = 'Required';
    if (touchedFields.listening && !fieldValues.listening) errs.listening = 'Required';
    if (touchedFields.writing && !fieldValues.writing) errs.writing = 'Required';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  useEffect(() => {
    if (!validationInit.current) {
      validate(exam);
    } else {
      validationInit.current = false;
    }
  }, [exam, touched]);

  useEffect(() => {
    if (!exam.startDate || !isAutoSchedule) return;
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }

    const schedule = generateSchedule(exam.startDate);
    const writingEndTime = schedule[2].endTime; // Get writing section's end time

    setExam((prev) => ({
      ...prev,
      endDate: writingEndTime,
      schedule: schedule,
    }));
  }, [exam.startDate, isAutoSchedule]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = value instanceof Date ? value : value;
    
    // Debug logging for date changes
    if (name === 'startDate' || name === 'endDate') {
      console.log(`Date change for ${name}:`, {
        value,
        type: typeof value,
        isDate: value instanceof Date,
        stringValue: value?.toString()
      });
    }
    
    setTouched((prev) => ({ ...prev, [name]: true }));
    setExam((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : val,
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleScheduleChange = (index, field, value) => {
    setExam((prev) => {
      const newSchedule = [...prev.schedule];
      newSchedule[index] = {
        ...newSchedule[index],
        [field]: value,
      };
      return { ...prev, schedule: newSchedule };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allTouched = {
      name: true,
      startDate: true,
      endDate: true,
      reading: true,
      listening: true,
      writing: true,
    };
    
    // Validate with all fields marked as touched
    if (!validate(exam, allTouched)) {
      setTouched(allTouched);
      showToast('Please fix the highlighted errors', 'error');
      return;
    }
    
    setTouched(allTouched);
    setSaving(true);

    // Debug logging
    console.log('Original exam dates:', {
      startDate: exam.startDate,
      endDate: exam.endDate,
      startDateType: typeof exam.startDate,
      endDateType: typeof exam.endDate
    });

    const examToSubmit = {
      ...exam,
      startDate: localToUTC(exam.startDate),
      endDate: localToUTC(exam.endDate),
      partnerId: exam.scope === 'PARTNER' && exam.partnerId ? exam.partnerId : null,
      contentBatchId: exam.contentBatchId !== '' && exam.contentBatchId !== null ? Number(exam.contentBatchId) : null,
      schedule: exam.schedule.map((item) => ({
        ...item,
        startTime: item.startTime ? localToUTC(item.startTime) : null,
        endTime: item.endTime ? localToUTC(item.endTime) : null,
      })),
    };

    console.log('Converted exam dates:', {
      startDate: examToSubmit.startDate,
      endDate: examToSubmit.endDate
    });

    try {
      if (id) {
        await updateExam(id, examToSubmit);
        showToast("Exam updated successfully!", "success");
      } else {
        await createExam(examToSubmit);
        showToast("Exam created successfully!", "success");
      }
      navigate("/exams");
    } catch (error) {
      console.error('Failed to save exam:', error);
      showToast("Failed to save exam", "error");
      setSaving(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
      <Typography>Loading...</Typography>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header Section */}
        <Fade in timeout={500}>
          <Box sx={{ mb: 4 }}>
            <Breadcrumbs sx={{ mb: 3 }}>
              <Link component={RouterLink} to="/exams" underline="hover" color="inherit">
                Exam List
              </Link>
              <Typography color="text.primary">{id ? 'Edit Exam' : 'Create Exam'}</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Zoom in timeout={600}>
                <Avatar
                  sx={{
                    backgroundColor: 'primary.main',
                    width: 56,
                    height: 56,
                    mr: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                >
                  <EventNoteIcon fontSize="large" />
                </Avatar>
              </Zoom>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                  {id ? 'Edit Exam' : 'Create New Exam'}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {id ? 'Update exam details and settings' : 'Set up a new IELTS exam with questions and schedule'}
                </Typography>
              </Box>
              <Chip
                label={`${Math.round(formProgress)}% Complete`}
                color={formProgress === 100 ? 'success' : 'primary'}
                icon={formProgress === 100 ? <CheckCircleIcon /> : undefined}
                sx={{ fontWeight: 600, px: 1 }}
              />
            </Box>

            {/* Progress Bar */}
            <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                  Form Completion
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {Math.round(formProgress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={formProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: formProgress === 100
                      ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
                      : 'linear-gradient(90deg, #2196f3 0%, #42a5f5 100%)',
                  }
                }}
              />
            </Paper>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          {/* Main Form */}
          <Grid item xs={12} lg={8}>
            <Fade in timeout={700}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'primary.main', width: 40, height: 40, mr: 2 }}>
                      <EventNoteIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', flexGrow: 1 }}>
                      Exam Details
                    </Typography>
                    {completedSections.basic && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Complete"
                        color="success"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    <Tooltip title="Enter basic exam information including name and schedule">
                      <InfoIcon sx={{ ml: 1, color: 'action.active', cursor: 'pointer' }} fontSize="small" />
                    </Tooltip>
                  </Box>
                
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <ModernTextField
                        label="Exam Name"
                        name="name"
                        value={exam.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                        placeholder="Enter exam name (e.g., IELTS Practice Test #1)"
                        required
                        InputProps={{
                          endAdornment: touched.name && exam.name && !errors.name && (
                            <CheckCircleIcon color="success" />
                          ),
                        }}
                      />
                    </Grid>

                    {/* Schedule Mode Selector */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                          Schedule Mode
                        </Typography>
                        <ToggleButtonGroup
                          color="primary"
                          exclusive
                          value={isAutoSchedule ? 'auto' : 'manual'}
                          onChange={(e, val) => {
                            if (val !== null) {
                              setIsAutoSchedule(val === 'auto');
                            }
                          }}
                          sx={{
                            gap: 2,
                            '& .MuiToggleButton-root': {
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                              px: 3,
                              py: 1.5,
                              border: '2px solid',
                              borderColor: 'grey.300',
                              margin: '0 4px',
                              minWidth: '140px',
                              '&.Mui-selected': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                                borderColor: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.dark',
                                },
                              },
                              '&:hover': {
                                backgroundColor: 'primary.50',
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                        >
                          <ToggleButton value="auto">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              Automatic
                              <Chip label="Recommended" size="small" color="success" variant="outlined" />
                            </Box>
                          </ToggleButton>
                          <ToggleButton value="manual">Manual</ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                          {isAutoSchedule
                            ? '✓ Listening starts 1 min after exam start (30 min) → Reading starts 1 min later (60 min) → Writing starts 1 min later (60 min)'
                            : '✓ You can manually customize all schedule times'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="Start Time"
                        value={exam.startDate}
                        onChange={(newValue) => {
                          console.log('DateTimePicker onChange:', newValue);
                          handleChange({ target: { name: 'startDate', value: newValue } });
                        }}
                        format="dd/MM/yyyy hh:mm a"
                        minDateTime={new Date()}
                        ampm={true}
                        slotProps={{
                          textField: {
                            name: "startDate",
                            onBlur: handleBlur,
                            error: Boolean(errors.startDate),
                            helperText: errors.startDate || "Select exam start date and time",
                            fullWidth: true,
                          },
                          openPickerButton: {
                            color: "primary"
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="End Time"
                        value={exam.endDate}
                        disabled={isAutoSchedule}
                        onChange={(newValue) => {
                          handleChange({ target: { name: 'endDate', value: newValue } });
                        }}
                        format="dd/MM/yyyy hh:mm a"
                        ampm={true}
                        slotProps={{
                          textField: {
                            name: "endDate",
                            helperText: isAutoSchedule ? 'Auto-set to writing section end time (153 min total)' : 'Select exam end date and time',
                            fullWidth: true,
                          },
                          openPickerButton: {
                            color: "primary"
                          }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <ModernTextField
                        label="Note"
                        name="note"
                        value={exam.note}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                        rows={3}
                        placeholder="Add any additional notes or instructions for this exam"
                      />
                    </Grid>
                  </Grid>

                  {completedSections.basic && (
                    <Grid item xs={12}>
                      <Fade in>
                        <Alert severity="success" sx={{ mt: 2 }}>
                          Exam details completed successfully!
                        </Alert>
                      </Fade>
                    </Grid>
                  )}
                </form>
              </CardContent>
            </Card>
            </Fade>

            {/* Content Selection */}
            <Fade in timeout={800}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'info.main', width: 40, height: 40, mr: 2 }}>
                      <InfoIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', flexGrow: 1 }}>
                      Content Selection
                    </Typography>
                    {completedSections.content && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Complete"
                        color="success"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    <Tooltip title="Select reading, listening, and writing sections for this exam">
                      <HelpOutlineIcon sx={{ ml: 1, color: 'action.active', cursor: 'pointer' }} fontSize="small" />
                    </Tooltip>
                  </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <ModernTextField
                      select
                      label="Reading"
                      name="reading"
                      value={exam.reading}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.reading)}
                      helperText={errors.reading}
                      required
                      InputProps={{
                        endAdornment: exam.reading && !errors.reading && (
                          <CheckCircleIcon color="success" sx={{ mr: 3 }} />
                        ),
                      }}
                    >
                      <MenuItem value="">Select Reading</MenuItem>
                      {readingOptions.map((option) => (
                        <MenuItem key={option._id} value={option._id}>{option.label}</MenuItem>
                      ))}
                    </ModernTextField>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ModernTextField
                      select
                      label="Listening"
                      name="listening"
                      value={exam.listening}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.listening)}
                      helperText={errors.listening}
                      required
                      InputProps={{
                        endAdornment: exam.listening && !errors.listening && (
                          <CheckCircleIcon color="success" sx={{ mr: 3 }} />
                        ),
                      }}
                    >
                      <MenuItem value="">Select Listening</MenuItem>
                      {listeningOptions.map((option) => (
                        <MenuItem key={option._id} value={option._id}>{option.label}</MenuItem>
                      ))}
                    </ModernTextField>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ModernTextField
                      select
                      label="Writing"
                      name="writing"
                      value={exam.writing}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.writing)}
                      helperText={errors.writing}
                      required
                      InputProps={{
                        endAdornment: exam.writing && !errors.writing && (
                          <CheckCircleIcon color="success" sx={{ mr: 3 }} />
                        ),
                      }}
                    >
                      <MenuItem value="">Select Writing</MenuItem>
                      {writingOptions.map((option) => (
                        <MenuItem key={option._id} value={option._id}>{option.label}</MenuItem>
                      ))}
                    </ModernTextField>
                  </Grid>
                </Grid>

                {completedSections.content && (
                  <Fade in>
                    <Alert severity="success" sx={{ mt: 3 }}>
                      Content selection completed! All sections have been assigned.
                    </Alert>
                  </Fade>
                )}
              </CardContent>
            </Card>
            </Fade>

            {/* Exam Type & Status Settings */}
            <Fade in timeout={900}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'warning.main', width: 40, height: 40, mr: 2 }}>
                      <ScheduleIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Exam Type & Status
                    </Typography>
                  </Box>

                <Grid container spacing={3}>
                  {/* Exam Type Dropdown */}
                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      select
                      label="Exam Type"
                      name="examType"
                      value={exam.examType}
                      onChange={handleChange}
                      required
                      helperText="Select the type of exam"
                    >
                      <MenuItem value="free-practice">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="FP" size="small" sx={{ backgroundColor: 'info.main', color: 'white' }} />
                          Free Practice
                        </Box>
                      </MenuItem>
                      <MenuItem value="paid-instant">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="PI" size="small" sx={{ backgroundColor: 'warning.main', color: 'white' }} />
                          Paid Instant
                        </Box>
                      </MenuItem>
                      <MenuItem value="paid-scheduled">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="PS" size="small" sx={{ backgroundColor: 'success.main', color: 'white' }} />
                          Paid Scheduled
                        </Box>
                      </MenuItem>
                    </ModernTextField>
                  </Grid>

                  {/* Region Dropdown */}
                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      select
                      label="Region"
                      name="region"
                      value={exam.region}
                      onChange={handleChange}
                      helperText="Geographic region for this exam"
                    >
                      <MenuItem value="worldwide">Worldwide</MenuItem>
                      <MenuItem value="europe">Europe</MenuItem>
                      <MenuItem value="asia">Asia</MenuItem>
                      <MenuItem value="americas">Americas</MenuItem>
                      <MenuItem value="middle-east">Middle East</MenuItem>
                    </ModernTextField>
                  </Grid>

                  {/* Status Checkboxes - only show for paid-scheduled exams */}
                  {exam.examType === 'paid-scheduled' && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 2, mb: 1 }}>
                          Scheduled Exam Status
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isCompleted"
                              checked={exam.isCompleted}
                              onChange={handleChange}
                              sx={{ '&.Mui-checked': { color: 'success.main' } }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">Completed</Typography>
                              {exam.isCompleted && <Chip label="Yes" size="small" color="success" />}
                            </Box>
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isActive"
                              checked={exam.isActive}
                              onChange={handleChange}
                              sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">Active</Typography>
                              {exam.isActive && <Chip label="Yes" size="small" color="primary" />}
                            </Box>
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isNext"
                              checked={exam.isNext}
                              onChange={handleChange}
                              sx={{ '&.Mui-checked': { color: 'warning.main' } }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">Upcoming</Typography>
                              {exam.isNext && <Chip label="Yes" size="small" color="warning" />}
                            </Box>
                          }
                        />
                      </Grid>
                    </>
                  )}

                  {/* For non-scheduled exams, just show isActive */}
                  {exam.examType !== 'paid-scheduled' && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="isActive"
                            checked={exam.isActive}
                            onChange={handleChange}
                            sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">Active</Typography>
                            {exam.isActive && <Chip label="Yes" size="small" color="primary" />}
                          </Box>
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
            </Fade>

            {/* Access & Scope */}
            <Fade in timeout={1000}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'secondary.main', width: 40, height: 40, mr: 2 }}>
                      <BusinessIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Access & Scope
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    {/* Scope */}
                    <Grid item xs={12} md={4}>
                      <ModernTextField
                        select
                        label="Scope"
                        name="scope"
                        value={exam.scope}
                        onChange={(e) => {
                          const newScope = e.target.value;
                          setExam((prev) => ({
                            ...prev,
                            scope: newScope,
                            partnerId: newScope === 'PUBLIC' ? '' : prev.partnerId,
                          }));
                          setTouched((prev) => ({ ...prev, scope: true }));
                        }}
                        helperText="Who can access this exam"
                      >
                        <MenuItem value="PUBLIC">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PublicIcon fontSize="small" />
                            Public
                          </Box>
                        </MenuItem>
                        <MenuItem value="PARTNER">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon fontSize="small" />
                            Partner Only
                          </Box>
                        </MenuItem>
                      </ModernTextField>
                    </Grid>

                    {/* Partner — only shown when scope is PARTNER */}
                    {exam.scope === 'PARTNER' && (
                      <Grid item xs={12} md={4}>
                        <ModernTextField
                          select
                          label="Partner"
                          name="partnerId"
                          value={exam.partnerId}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText="Select the partner this exam belongs to"
                        >
                          <MenuItem value="">Select Partner</MenuItem>
                          {partnerOptions.map((p) => (
                            <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
                          ))}
                        </ModernTextField>
                      </Grid>
                    )}

                    {/* Content Batch ID */}
                    <Grid item xs={12} md={4}>
                      <ModernTextField
                        label="Content Batch ID"
                        name="contentBatchId"
                        type="number"
                        value={exam.contentBatchId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Optional batch identifier"
                        helperText="Group exams into a content batch (optional)"
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Fade>

            {/* Floating Action Bar */}
            <Paper
              elevation={3}
              sx={{
                position: 'sticky',
                bottom: 20,
                zIndex: 10,
                p: 2.5,
                borderRadius: 3,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
              }}
            >
              <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon color="primary" fontSize="small" />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {formProgress === 100 ? 'All required fields completed!' : 'Fill all required fields to enable save'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                  <ModernButton
                    component={RouterLink}
                    to="/exams"
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back to List
                  </ModernButton>
                  <ModernButton
                    onClick={handleSubmit}
                    variant="contained"
                    startIcon={<SaveIcon />}
                    loading={saving}
                    disabled={!completedSections.basic || !completedSections.content}
                    sx={{
                      minWidth: 180,
                      background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      },
                    }}
                  >
                    {id ? 'Update Exam' : 'Create Exam'}
                  </ModernButton>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Schedule Preview/Editor */}
          <Grid item xs={12} lg={4}>
            {exam.startDate && (
              <Zoom in timeout={900}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    position: 'sticky',
                    top: 100,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar
                        sx={{
                          backgroundColor: 'success.main',
                          width: 40,
                          height: 40,
                          mr: 2,
                        }}
                      >
                        <TimelineIcon />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', flexGrow: 1 }}>
                        Exam Schedule
                      </Typography>
                      {completedSections.schedule && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Set"
                          color="success"
                          size="small"
                        />
                      )}
                    </Box>

                    {/* Schedule Mode Indicator */}
                    <Paper
                      sx={{
                        p: 2,
                        mb: 3,
                        backgroundColor: isAutoSchedule
                          ? (theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.15)' : 'success.50')
                          : (theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'primary.50'),
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: isAutoSchedule ? 'success.main' : 'primary.main',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon sx={{ color: isAutoSchedule ? 'success.main' : 'primary.main' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isAutoSchedule ? 'success.main' : 'primary.main',
                          }}
                        >
                          {isAutoSchedule ? 'Automatic Schedule' : 'Manual Schedule'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                        {isAutoSchedule
                          ? 'Times are automatically calculated based on exam start time'
                          : 'You can manually adjust all section times'}
                      </Typography>
                    </Paper>

                  {isAutoSchedule ? (
                    // Auto Mode: Read-only preview
                    exam.schedule.map((item, idx) => (
                      <Fade in timeout={1000 + idx * 100} key={idx}>
                        <Box>
                          <Paper
                            sx={{
                              p: 3,
                              mb: 2,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: theme.palette.mode === 'dark' ? 'grey.100' : 'grey.50',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.08)',
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Chip
                                label={item.sectionType}
                                color="primary"
                                size="small"
                                sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                              />
                              <CheckCircleIcon
                                sx={{ ml: 'auto', color: 'success.main' }}
                                fontSize="small"
                              />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatForDisplay(item.startTime)}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: 'text.secondary', pl: 4 }}>
                                to {formatForDisplay(item.endTime)}
                              </Typography>
                            </Box>
                          </Paper>
                          {idx < exam.schedule.length - 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                              <Divider sx={{ width: '50%' }} />
                            </Box>
                          )}
                        </Box>
                      </Fade>
                    ))
                  ) : (
                    // Manual Mode: Editable time pickers
                    exam.schedule.map((item, idx) => (
                      <Box key={idx}>
                        <Paper
                          sx={{
                            p: 3,
                            mb: 2,
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: 'primary.main',
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'primary.50',
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, textTransform: 'capitalize', color: 'primary.main' }}>
                            {item.sectionType} Section
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <DateTimePicker
                              label="Start Time"
                              value={item.startTime}
                              onChange={(newValue) => handleScheduleChange(idx, 'startTime', newValue)}
                              format="dd/MM/yyyy hh:mm a"
                              ampm={true}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: true,
                                  helperText: "Select section start time",
                                },
                                openPickerButton: {
                                  color: "primary"
                                }
                              }}
                            />
                            <DateTimePicker
                              label="End Time"
                              value={item.endTime}
                              onChange={(newValue) => handleScheduleChange(idx, 'endTime', newValue)}
                              format="dd/MM/yyyy hh:mm a"
                              ampm={true}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: true,
                                  helperText: "Select section end time",
                                },
                                openPickerButton: {
                                  color: "primary"
                                }
                              }}
                            />
                          </Box>
                        </Paper>
                        {idx < exam.schedule.length - 1 && <Divider sx={{ my: 1 }} />}
                      </Box>
                    ))
                  )}
                  </CardContent>
                </Card>
              </Zoom>
            )}
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default ExamForm;
